import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { X, UserRoundPlus, Info, ShieldCheck } from 'lucide-react';
import { createMocPatient } from '../../redux/mocSlice';
import { getCountries } from '../../redux/masterSlice';
import CountrySelect from '../../components/common/CountrySelect';
import { sanitizePostalCode, POSTAL_CODE_MAX_LENGTH } from '../../utils/personFields';
import { MOC_PATIENT_POLICY, missingAnyContact } from '../../utils/loginPolicy';

// SUPER ADMIN CREATES ONE PERSON (owner, 2026-09-02; re-grounded 22 Sep 2026).
//
// What this makes is the PERSON — one `patients` row, `kind='moc'`, with a
// Mitra One Care id and no hospital (doc/one-patient-row.md §2). A hospital
// gives them a patient number when they first visit; nothing is "created at" a
// hospital here or later. The row is UNCLAIMED until the person signs in for
// themselves, which is what still lets the platform correct what it typed.
//
// THE FIELDS ARE THE ONES THE API ACCEPTS, no more — anything else would be
// dropped in silence. POST /moc-admin/patients (validateMocPatientInput in
// mocPatientAdminController) takes name, surname, email, phone, gender, dob and,
// since the profile moved onto the person row, address, city, zipCode,
// countryId, bloodGroup, altPhone and occupation. All of the second group are
// OPTIONAL: the desk often knows the address and the blood group, and typing
// them here saves the patient doing it — but a blank is a blank, not an error.
// The country is validated against the Country master server-side (400
// INVALID_COUNTRY), which is why this form picks from that same master rather
// than accepting free text.
//
// Two things the form deliberately does NOT offer, and the note below says so:
//   * verified ticks — verification comes from OTP proofs only;
//   * a password box — the server mints a temporary one and sends it with the
//     login id(s) (owner, 2 Oct 2026); the patient's FIRST SIGN-IN is the
//     activation: a code on every contact not yet verified, then their own
//     password. Until then the account can do nothing else.
// THE MITRA ONE CARE PATIENT RULE (utils/loginPolicy#MOC_PATIENT_POLICY):
// at least one of email / mobile, both allowed, no code from the operator —
// patients come from India and abroad, so a mobile is never mandatory. A
// person with no contact at all can never sign in AND can never recover —
// unreachable even by themselves — so that one is refused before the request.

const GENDER_OPTIONS = ['male', 'female', 'other', 'Not_Communicated'];

// The eight the API accepts, in the order a donor card prints them.
const BLOOD_GROUPS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const EMPTY = {
  name: '', surname: '', email: '', phone: '', gender: '', dob: '',
  address: '', city: '', zipCode: '', countryId: '', bloodGroup: '',
  altPhone: '', occupation: '',
};

export default function MocAddPatientModal({ isOpen, onClose, onComplete }) {
  const { t } = useTranslation(['patientpage']);
  const dispatch = useDispatch();
  // The same active-country master every other form on this app picks from
  // (AdminProfileTab, PatientProfileTab) — the server validates the id against
  // it, so a list built anywhere else could offer a value the save refuses.
  const { countries = [] } = useSelector((s) => s.master || {});

  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && countries.length === 0) dispatch(getCountries({ status: 'active', limit: 100 }));
  }, [isOpen, countries.length, dispatch]);

  if (!isOpen) return null;

  const handleClose = () => {
    setForm(EMPTY);
    setErrors({});
    setSaving(false);
    onClose();
  };

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = t('moc.add_name_required', 'Name is required');
    if (missingAnyContact(MOC_PATIENT_POLICY, { hasEmail: !!form.email.trim(), hasPhone: !!form.phone.trim() })) {
      next.email = t('moc.add_contact_required', 'Give an email or a mobile number — at least one');
      next.phone = t('moc.add_contact_required', 'Give an email or a mobile number — at least one');
    }
    if (form.email.trim() && !/.+@.+\..+/.test(form.email.trim())) {
      next.email = t('moc.add_email_invalid', 'That email does not look right');
    }
    const digits = form.phone.replace(/[^0-9]/g, '');
    if (form.phone.trim() && (digits.length < 6 || digits.length > 15)) {
      next.phone = t('moc.add_phone_invalid', 'That mobile number does not look right');
    }
    // Same bounds the API applies to the alternate number, checked here so the
    // operator is told before the round trip.
    const altDigits = form.altPhone.replace(/[^0-9]/g, '');
    if (form.altPhone.trim() && (altDigits.length < 6 || altDigits.length > 15)) {
      next.altPhone = t('moc.add_phone_invalid', 'That mobile number does not look right');
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSaving(true);
    try {
      await dispatch(createMocPatient({
        name: form.name.trim(),
        surname: form.surname.trim() || undefined,
        email: form.email.trim() || undefined,
        phone: form.phone.trim() || undefined,
        gender: form.gender || undefined,
        dob: form.dob || undefined,
        // The profile half. Blank means "not given", so it is left out of the
        // request entirely rather than sent as an empty string.
        address: form.address.trim() || undefined,
        city: form.city.trim() || undefined,
        zipCode: form.zipCode.trim() || undefined,
        countryId: form.countryId || undefined,
        bloodGroup: form.bloodGroup || undefined,
        altPhone: form.altPhone.trim() || undefined,
        occupation: form.occupation.trim() || undefined,
      })).unwrap();
      toast.success(t('moc.add_created', 'Patient created'));
      if (onComplete) onComplete();
      handleClose();
    } catch (err) {
      // No contact at all (400 IDENTIFIER_REQUIRED, or FIELD_REQUIRED parked on
      // one of the two): the answer belongs to BOTH contact fields, worded as
      // the form words it — either one will do.
      const noContact = err?.code === 'IDENTIFIER_REQUIRED'
        || (err?.code === 'FIELD_REQUIRED' && ['email', 'phone'].includes(err?.field));
      if (noContact) {
        const msg = t('moc.add_contact_required', 'Give an email or a mobile number — at least one');
        setErrors((prev) => ({ ...prev, email: msg, phone: msg }));
      } else if (err?.field) {
        setErrors((prev) => ({ ...prev, [err.field]: err.error }));
      }
      toast.warning(err?.error || t('moc.add_failed', 'Failed to create the patient'));
      setSaving(false);
    }
  };

  const inputClass = (field) =>
    `w-full rounded-xl border px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 ${errors[field]
      ? 'border-red-400 bg-red-50 focus:ring-red-300'
      : 'border-gray-200 focus:ring-[#4ABDBD]'}`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="moc-add-patient-modal">
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <UserRoundPlus className="w-5 h-5 text-[#3AA8A8]" />
            <h2 className="text-lg font-bold text-gray-800">{t('moc.add_title', 'Add a patient')}</h2>
          </div>
          <button data-testid="moc-add-patient-close" onClick={handleClose} className="text-gray-400 hover:text-gray-600 cursor-pointer">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4" data-testid="moc-add-patient-form">
          <div className="flex items-start gap-2.5 rounded-xl border-l-4 border-[#4ABDBD] bg-[#4ABDBD]/10 p-3.5">
            <Info className="mt-0.5 h-4 w-4 flex-shrink-0 text-[#2D8A8A]" />
            <p className="text-xs leading-relaxed text-[#2D8A8A]">
              {t('moc.add_note',
                'This creates the person’s Mitra One Care profile. A hospital will see it and give them a patient number there when they visit. An e-mail or a mobile number is enough — no code is needed here. The patient receives their login and a temporary password; at their first sign-in they verify their contact details and choose their own password. They fill the rest of the profile — address, blood group and the rest — in their own app.')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                {t('patientpage.name', 'Name')} <span className="text-red-500">*</span>
              </label>
              <input
                data-testid="moc-add-patient-name"
                type="text"
                value={form.name}
                onChange={(e) => setField('name', e.target.value)}
                maxLength={40}
                placeholder={t('moc.add_name_ph', 'First name')}
                className={inputClass('name')}
              />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('moc.add_surname', 'Surname')}</label>
              <input
                data-testid="moc-add-patient-surname"
                type="text"
                value={form.surname}
                onChange={(e) => setField('surname', e.target.value)}
                maxLength={40}
                placeholder={t('moc.add_surname_ph', 'Family name')}
                className={inputClass('surname')}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('patientpage.email', 'Email')}</label>
              <input
                data-testid="moc-add-patient-email"
                type="email"
                value={form.email}
                onChange={(e) => setField('email', e.target.value)}
                maxLength={254}
                placeholder="name@example.com"
                className={inputClass('email')}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('patientpage.phone', 'Phone')}</label>
              <input
                data-testid="moc-add-patient-phone"
                type="tel"
                value={form.phone}
                onChange={(e) => setField('phone', e.target.value.replace(/[^0-9+\s]/g, ''))}
                maxLength={18}
                placeholder="+91 98765 43210"
                className={inputClass('phone')}
              />
              {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('patientpage.gender', 'Gender')}</label>
              <select
                data-testid="moc-add-patient-gender"
                value={form.gender}
                onChange={(e) => setField('gender', e.target.value)}
                className={`${inputClass('gender')} cursor-pointer bg-white`}
              >
                <option value="">{t('moc.add_gender_ph', 'Not given')}</option>
                {GENDER_OPTIONS.map((g) => (
                  <option key={g} value={g}>{t(`patientpage.${g}`, g)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('patientpage.dob', 'DOB')}</label>
              <input
                data-testid="moc-add-patient-dob"
                type="date"
                value={form.dob}
                onChange={(e) => setField('dob', e.target.value)}
                min="1900-01-01"
                max={new Date().toISOString().split('T')[0]}
                className={inputClass('dob')}
              />
              {errors.dob && <p className="text-red-500 text-xs mt-1">{errors.dob}</p>}
            </div>
          </div>

          <p className="-mt-1 flex items-start gap-1.5 text-xs text-gray-500" data-testid="moc-add-patient-first-signin-hint">
            <ShieldCheck className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-[#2D8A8A]" />
            <span>{t('moc.add_first_signin_hint', 'The patient verifies their contact and sets a password at their first sign-in.')}</span>
          </p>

          {/* THE PROFILE HALF — optional, and the reason it is here: the desk
              usually knows these at the counter, and the person row is the one
              place they live now. Everything below is accepted by the API; a
              blank is simply left blank and the patient fills it in later. */}
          <p className="pt-1 text-xs font-bold uppercase tracking-wide text-gray-400">
            {t('moc.add_profile_section', 'Profile (optional)')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('patientprofiletab.address', 'Address')}</label>
              <input
                data-testid="moc-add-patient-address"
                type="text"
                value={form.address}
                onChange={(e) => setField('address', e.target.value)}
                maxLength={100}
                className={inputClass('address')}
              />
              {errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('patientprofiletab.city', 'City')}</label>
              <input
                data-testid="moc-add-patient-city"
                type="text"
                value={form.city}
                onChange={(e) => setField('city', e.target.value)}
                maxLength={60}
                className={inputClass('city')}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('patientprofiletab.zipcode', 'Postal code')}</label>
              <input
                data-testid="moc-add-patient-zipCode"
                type="text"
                value={form.zipCode}
                // Alphanumeric only and capped, the same rule the profile form
                // uses — postcodes differ by country but never carry punctuation.
                onChange={(e) => setField('zipCode', sanitizePostalCode(e.target.value))}
                maxLength={POSTAL_CODE_MAX_LENGTH}
                className={inputClass('zipCode')}
              />
            </div>
            <div data-testid="moc-add-patient-country">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('patientprofiletab.country', 'Country')}</label>
              <CountrySelect
                value={form.countryId}
                onChange={(countryId) => setField('countryId', countryId || '')}
                countries={countries}
                placeholder={t('patientprofiletab.select_country', 'Select Country')}
                error={!!errors.countryId}
              />
              {errors.countryId && <p className="text-red-500 text-xs mt-1">{errors.countryId}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('patientprofiletab.blood_group', 'Blood group')}</label>
              <select
                data-testid="moc-add-patient-bloodGroup"
                value={form.bloodGroup}
                onChange={(e) => setField('bloodGroup', e.target.value)}
                className={`${inputClass('bloodGroup')} cursor-pointer bg-white`}
              >
                <option value="">{t('moc.add_gender_ph', 'Not given')}</option>
                {BLOOD_GROUPS.map((g) => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
              {errors.bloodGroup && <p className="text-red-500 text-xs mt-1">{errors.bloodGroup}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('patientprofiletab.alt_phone', 'Alternate phone')}</label>
              <input
                data-testid="moc-add-patient-altPhone"
                type="tel"
                value={form.altPhone}
                onChange={(e) => setField('altPhone', e.target.value.replace(/[^0-9+\s]/g, ''))}
                maxLength={18}
                placeholder="+91 98765 43210"
                className={inputClass('altPhone')}
              />
              {errors.altPhone && <p className="text-red-500 text-xs mt-1">{errors.altPhone}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">{t('patientprofiletab.occupation', 'Occupation')}</label>
              <input
                data-testid="moc-add-patient-occupation"
                type="text"
                value={form.occupation}
                onChange={(e) => setField('occupation', e.target.value)}
                maxLength={25}
                className={inputClass('occupation')}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              data-testid="moc-add-patient-cancel"
              onClick={handleClose}
              className="px-4 py-2 rounded-xl border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-semibold cursor-pointer"
            >
              {t('moc.add_cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              data-testid="moc-add-patient-submit"
              disabled={saving}
              className={`px-4 py-2 rounded-xl text-white text-sm font-semibold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] ${saving ? 'opacity-60 cursor-not-allowed' : 'hover:shadow-lg cursor-pointer'}`}
            >
              {saving ? t('moc.add_saving', 'Creating…') : t('moc.add_submit', 'Create patient')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
